import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  BookOpen,
  GraduationCap,
  CheckCircle,
  Target,
  Eye,
  Lock,
} from 'lucide-react';
import { CyberGrid } from '../components/CyberGrid';

const Academy: React.FC = () => {
  const tracks = [
    {
      icon: Shield,
      title: 'Vulnerability Management Foundations',
      description:
        'A practical introduction to scanning, prioritization, remediation workflows, and reporting—built for real-world programs.',
      bullets: ['Asset coverage basics', 'Risk-based triage', 'Remediation playbooks', 'Program reporting'],
    },
    {
      icon: Lock,
      title: 'Security Program & Compliance Readiness',
      description:
        'Learn how to communicate risk, align controls, and prepare for audits without bloated documentation.',
      bullets: ['Control mapping', 'Evidence workflows', 'Policy essentials', 'Audit-ready reporting'],
    },
    {
      icon: Target,
      title: 'Risk Prioritization & Decision Support',
      description:
        'Turn vulnerability noise into decisions: exploitability, business context, and measurable reduction in exposure.',
      bullets: ['Prioritization models', 'Risk acceptance', 'Executive summaries', 'KPIs & trends'],
    },
  ];

  const offerings = [
    {
      icon: BookOpen,
      title: 'Guides & Playbooks',
      description:
        'Short, enterprise-ready references: triage logic, remediation workflow templates, and reporting structures.',
    },
    {
      icon: GraduationCap,
      title: 'Workshops (Live / Remote)',
      description:
        'Team sessions focused on implementing a repeatable vulnerability management process and improving MTTR.',
    },
    {
      icon: Eye,
      title: 'Monthly Briefings',
      description:
        'Practical insights on trends, exploit activity, and operational lessons—focused on action, not headlines.',
    },
  ];

  return (
    <div className="w-full">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white overflow-hidden">
        <CyberGrid tone="dark" animate opacity={0.09} wash={false} className="z-0" />

        {/* Soft spotlights (static) */}
        <div aria-hidden className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute -top-24 left-10 w-[520px] h-[520px] rounded-full bg-cyan-400/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-[520px] h-[520px] rounded-full bg-indigo-400/10 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="py-16 md:py-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-slate-200 text-sm font-semibold backdrop-blur-sm">
              <Shield size={16} className="text-cyan-300" />
              <span>CYVANTIX ACADEMY</span>
            </div>

            <h1 className="mt-6 text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight text-white max-w-3xl">
              Practical Security Training Built for Real Programs
            </h1>

            <p className="mt-5 text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
              Short, enterprise-focused learning for vulnerability management, risk prioritization,
              and compliance readiness—designed for teams who need clarity and outcomes.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center px-6 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl"
              >
                Request Training / Workshop
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Link>

              <Link
                to="/services"
                className="inline-flex items-center justify-center px-6 py-3 border border-white/25 text-white font-semibold rounded-xl hover:bg-white/10 transition-all"
              >
                Explore Consulting Services
              </Link>
            </div>

            {/* Trust bar */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <CheckCircle className="w-5 h-5 text-cyan-300" />
                <span className="text-slate-200">Operational, not theoretical</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <Target className="w-5 h-5 text-indigo-300" />
                <span className="text-slate-200">Risk-first prioritization</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <BookOpen className="w-5 h-5 text-slate-200" />
                <span className="text-slate-200">Templates & playbooks included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100/60">
        <CyberGrid opacity={0.06} wash className="z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1.5 bg-gradient-to-r from-slate-900 to-slate-700 rounded-full text-white text-xs font-bold mb-4 shadow-lg">
              LEARNING OPTIONS
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              What You’ll Find in the Academy
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Built for security teams who need a repeatable program: clear guidance, standardized workflows,
              and practical deliverables.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {offerings.map((o, idx) => {
              const Icon = o.icon;
              return (
                <div
                  key={idx}
                  className="relative bg-white/90 backdrop-blur-sm rounded-2xl border border-slate-200 shadow-md p-8"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-xl flex items-center justify-center mb-5 shadow-lg">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-extrabold text-slate-900 mb-3">{o.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{o.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRACKS */}
      <section className="relative py-16 md:py-20 bg-white overflow-hidden">
        <CyberGrid opacity={0.035} wash className="z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Recommended Learning Tracks
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Choose a track based on where your program is today. Each track can be delivered as
              a workshop or used as a self-paced guide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tracks.map((t, idx) => {
              const Icon = t.icon;
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-slate-200 shadow-md p-8"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-extrabold text-slate-900">{t.title}</h3>
                  </div>

                  <p className="text-slate-600 text-sm leading-relaxed mb-5">{t.description}</p>

                  <ul className="space-y-2">
                    {t.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 mt-0.5 text-slate-900" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-7 py-3 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-all shadow-lg"
            >
              Talk to Us About Training
              <ArrowRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white py-20 md:py-24 overflow-hidden">
        <CyberGrid tone="dark" opacity={0.07} wash={false} className="z-0" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-slate-200 text-sm font-bold mb-6">
              <Shield size={16} className="text-cyan-300" />
              <span>READY TO LEVEL UP YOUR PROGRAM?</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-white">
              Turn Training Into Measurable Security Outcomes
            </h2>

            <p className="text-xl text-slate-300 mb-10 leading-relaxed">
              If your team is overwhelmed by vulnerability noise, Cyvantix Academy helps build a
              repeatable workflow—prioritize, remediate, and report with confidence.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl"
              >
                Request Training
              </Link>

              <Link
                to="/services"
                className="inline-flex items-center justify-center px-8 py-3 border border-white/25 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Academy;
